// side effect imports to run first
import './why-did-you-render';
import 'styles';
import './polyfill';
import 'utils/accessibility/text-size';
import 'utils/keyboard/keyboard-config';

import { useEffect, useState } from 'react';

import { Asset } from 'expo-asset';
import { updateId } from 'expo-updates';
import { LogBox, Platform } from 'react-native';

import { EnvPicker } from 'components/env-picker';
import { DefaultHelmetHTML } from 'components/layout/helmet-html.native';
import { GenericMiscellaneous } from 'components/miscellaneous/generic-miscellaneous';
import { PlatformSpecificMiscellaneous } from 'components/miscellaneous/platform-specific-miscellaneous';
import { Wrapper } from 'components/miscellaneous/wrapper';
import ReduceProviders from 'components/reduce-providers';
import { useOtaLogger } from 'hooks/use-ota-logger';
import { GlobalAppModals, RootOnlyGlobalAppModals } from 'navigation/global-app-modals';
import { DialogStaticPage } from 'pages/authentication/components/dialog-static-page';
import { initializeAmplitude } from 'state/amplitude/initialize';
import { GlobalStylesheet } from 'styles/global';
import { theme } from 'styles/theme';
import { appVersion, binaryRuntimeAppVersionWithBuildNumber } from 'utils/app-version-info';
import { Cognito } from 'utils/cognito';
import NativeAuthStorage from 'utils/cognito/storage';
import crashlytics from 'utils/crashlytics';
import { initDatadog } from 'utils/datadog';
import { getDeviceId } from 'utils/device-id';
import { appStartId, shouldShowEnvPickerAtStart } from 'utils/environment';
import { EventName, emitEvent } from 'utils/event-hub';
import { initExpoFonts } from 'utils/expo';
import { handleFatalError, setupGlobalErrorHandler } from 'utils/fatal-error-handler';
import { WebLocalStorage } from 'utils/local-storage/web-local-storage';
import logger from 'utils/logger';
import { initializePerformanceMonitors } from 'utils/performance';
import { Measures, PerformanceMarks, logMeasurement, setMark } from 'utils/timing';

// TODO fix these as we will get better RUM stats if we do
LogBox.ignoreLogs([
  'DATADOG: An action event was dropped because either the `onPress` method arguments were undefined or they were missing the target information.',
  'DATADOG: A navigation change was detected but the RUM ViewEvent was dropped as the route was undefined.',
  /SerializableStateInvariantMiddleware took.*/,
]);
LogBox.ignoreAllLogs();

// Added this to prevent crash on Android
// See https://github.com/andyearnshaw/Intl.js#regexp-cache--restore
// and https://github.com/expo/expo/issues/6536
if (Platform.OS === 'android') {
  if (typeof (Intl as any).__disableRegExpRestore === 'function') {
    (Intl as any).__disableRegExpRestore();
  }
}

const setCrashlyticsAttributes = () => {
  crashlytics().setAttribute('binaryRuntimeVersion', binaryRuntimeAppVersionWithBuildNumber);
  crashlytics().setAttribute('appVersion', appVersion);
  crashlytics().setAttribute('updateId', updateId ?? 'UNKNOWN');
  crashlytics().setAttribute('appStartId', appStartId);
};

// SETUP datadog logging
try {
  try {
    initDatadog();
  } catch (error) {
    setCrashlyticsAttributes();
    crashlytics().recordError(new Error(`Failed to init datadog logging! ${error?.toString()}`));
  }
} catch (error) {}

// SETUP crashlytics
try {
  setCrashlyticsAttributes();
  getDeviceId().then(deviceId => crashlytics().setAttribute('deviceId', deviceId));
} catch (error) {
  logger.error({ error, message: 'Failed to set crashlytics attributes on app start' });
}

// SETUP global error handler
setupGlobalErrorHandler();

// SETUP performance monitors
initializePerformanceMonitors();
setMark(PerformanceMarks.APP_RN_TSX_START);
emitEvent(EventName.JAVASCRIPT_START);

initializeAmplitude();

// Startup congito with its configuration as early as possible.
Cognito.configure();

let appReadyPromiseResolved = false;
const appReadyPromise = Promise.all([
  NativeAuthStorage.sync(), // this is called internally by Cognito.configure() but it doesn't give us a promise!
  initExpoFonts(theme.cachedFonts),
  WebLocalStorage.sync(),
] as Array<Promise<Asset | void>>).then(() => (appReadyPromiseResolved = true));

export const AppInner: React.FC<{ routes: React.ReactNode }> = ({ routes }) => {
  const [isAppReady, setIsAppReady] = useState(appReadyPromiseResolved);
  useEffect(() => {
    const init = async () => {
      try {
        logger.info({ message: 'React app started', isAppStart: true });

        setMark(PerformanceMarks.APP_LOADING_START);

        await appReadyPromise;

        logMeasurement(Measures.APP_LOADING, PerformanceMarks.APP_LOADING_START);

        setIsAppReady(true);
        // eslint-disable-next-line prettier/prettier
      } catch (error: any) {
        handleFatalError('InitError', error);
      }
    };

    init();
  }, []);

  useOtaLogger();

  if (shouldShowEnvPickerAtStart()) {
    return <EnvPicker />;
  }

  if (!isAppReady) {
    return null;
  }

  return (
    <ReduceProviders>
      <Wrapper>
        {routes}
        <GlobalStylesheet />
        <DefaultHelmetHTML />
        <GlobalAppModals />
        <RootOnlyGlobalAppModals />
        <PlatformSpecificMiscellaneous />
        <GenericMiscellaneous />
        <DialogStaticPage />
      </Wrapper>
    </ReduceProviders>
  );
};
